import { DataTable } from "@/components/molecules/tables/data-table";
import {
  deviceKeys,
  useDeviceCallsPrompt,
  useDeviceCredentialsPrompt,
  useDevices,
  useRegenerateDeviceCredentials,
} from "@/features/devices/hooks/use-devices";
import { useClientId } from "@/features/utils/api-scope";
import { useTranslation } from "@/features/utils/i18n";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useDeviceColumns } from "./columns";
import { DeviceCallsDialog } from "./calls-dialog";
import { CreateDeviceDialog } from "./create-device-dialog";
import { DeviceCredentialsDialog } from "./credentials-dialog";

/**
 * The handsets of one client, with the dialogs the table opens.
 *
 * A component rather than a page so it can be mounted twice: on the client's
 * own VoIP page, and as a tab of the platform's client view — the same table,
 * the same dialogs, against whichever mount of the API the surrounding
 * `ApiScopeProvider` names. The columns thicken under `staff` (see
 * `./columns.tsx`); nothing else differs, on purpose.
 */
export const DevicesTable = () => {
  const { t } = useTranslation("devices");
  const clientId = useClientId();
  const { data: devices = [], isLoading } = useDevices(clientId || "");
  const columns = useDeviceColumns();

  // Mounted here, not in the row that issued them: a table re-render must not
  // be able to take away the one copy of a password that exists.
  const { prompt, showIssued, clear } = useDeviceCredentialsPrompt();
  const regenerate = useRegenerateDeviceCredentials();
  const { device: callsDevice, clear: clearCalls } = useDeviceCallsPrompt();
  const queryClient = useQueryClient();

  // The refresh waits for the dialog to close. Invalidating while it is open is
  // what replaces the table's data underneath it.
  const handleCredentialsClosed = () => {
    clear();
    queryClient.invalidateQueries({ queryKey: deviceKeys.all });
  };

  /**
   * Re-issue from inside the dialog, and swap it to showing the new password.
   *
   * Owned here rather than by the row for the same reason the dialog is: the
   * table can reconcile a row away on any refetch, and the response to this
   * mutation contains the only copy of a password that will ever exist.
   */
  const handleRegenerate = async () => {
    if (!clientId || !prompt) return;
    try {
      const result = await regenerate.mutateAsync({
        clientId,
        deviceId: prompt.deviceId,
      });
      showIssued(prompt.deviceId, result.credentials);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("devices.messages.credentialsFailed"),
      );
    }
  };

  return (
    <section className="space-y-3">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold">{t("devices.section")}</h3>
          <p className="text-muted-foreground text-sm">
            {t("devices.sectionDescription")}
          </p>
        </div>
        <CreateDeviceDialog />
      </div>

      <DataTable
        columns={columns}
        data={devices}
        isLoading={isLoading}
        hideSearch
      />

      <DeviceCallsDialog device={callsDevice} onClose={clearCalls} />

      <DeviceCredentialsDialog
        connection={prompt?.connection ?? null}
        password={prompt?.password}
        passwordHint={prompt?.passwordHint}
        onRegenerate={handleRegenerate}
        regenerating={regenerate.isPending}
        onClose={handleCredentialsClosed}
      />
    </section>
  );
};
